<template>
  <div class="rm-screen rm-gu">
    <div v-if="loadingCompanies && !companies.length" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!companies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-account-group-outline</v-icon>
      {{ $t("no_data_available") }}
      <span class="rm-muted">{{ $t("no_users_found") }}</span>
    </div>

    <template v-else>
      <button type="button" class="rm-card rm-gu-company" :aria-haspopup="'dialog'" @click="sheet = true">
        <v-icon size="20" color="#c2410c">mdi-domain</v-icon>
        <span class="rm-gu-company__text">
          <span class="rm-muted">{{ $t("firm") }}</span>
          <span class="rm-gu-company__name">{{ company ? toLatin(company) : $t("select_company") }}</span>
        </span>
        <v-icon size="18" color="#8a817a">mdi-chevron-down</v-icon>
      </button>

      <div v-if="loading" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else-if="!company" class="rm-empty">{{ $t("select_company") }}</div>
      <div v-else-if="!users.length" class="rm-empty">
        <template v-if="search">{{ $t("report_mobile.no_results_for", { search }) }}</template>
        <template v-else>
          <v-icon size="40" color="#8a817a">mdi-account-group-outline</v-icon>
          {{ $t("no_users_found") }}
        </template>
      </div>

      <template v-else>
        <div class="rm-stats">
          <div class="rm-stat rm-stat--inverse">
            <span class="rm-stat__label">{{ $t("users") }}</span>
            <span class="rm-stat__value rm-num">{{ users.length }}</span>
          </div>
          <div class="rm-stat">
            <span class="rm-stat__label">{{ $t("ordered") }}</span>
            <span class="rm-stat__value rm-num">{{ totals.ordered }}</span>
          </div>
          <div class="rm-stat">
            <span class="rm-stat__label">{{ $t("taken") }}</span>
            <span class="rm-stat__value rm-num">{{ totals.taken }}</span>
          </div>
        </div>

        <ul class="rm-list rm-card">
          <li v-for="user in users" :key="user.key" class="rm-gu-row">
            <span class="rm-avatar">{{ user.initials }}</span>
            <div class="rm-gu-row__body">
              <span class="rm-gu-row__name">{{ user.name }}</span>
              <span v-if="user.email" class="rm-muted rm-gu-row__email">{{ user.email }}</span>
              <span class="rm-gu-row__counts">
                <span class="rm-gu-count rm-num">
                  {{ $t("ordered") }} <b>{{ user.ordered }}</b>
                </span>
                <span class="rm-gu-count rm-num" :class="{ 'is-complete': user.complete }">
                  <v-icon v-if="user.complete" size="14" color="#15803d">mdi-check-circle</v-icon>
                  {{ $t("taken") }} <b>{{ user.taken }}</b>
                </span>
                <span class="rm-muted rm-num rm-gu-row__id">ID {{ user.id }}</span>
              </span>
            </div>
          </li>
        </ul>
      </template>
    </template>

    <ui-sheet v-model="sheet" :title="$t('select_company')" :closable="false">
      <ul class="rm-list rm-actions rm-gu-companies">
        <li v-for="name in companies" :key="name">
          <button
            type="button"
            class="rm-actions__row"
            :aria-pressed="String(name === company)"
            @click="pick(name)"
          >
            <v-icon size="22" :color="name === company ? '#c2410c' : 'transparent'">mdi-check</v-icon>
            <span class="rm-gu-companies__name">{{ toLatin(name) }}</span>
          </button>
        </li>
      </ul>
    </ui-sheet>
  </div>
</template>

<script>
import "./guestMobileLists.scss";

/**
 * Korisnici gostiju na telefonu: izbor firme (sheet) i spisak korisnika sa brojem poručenih i preuzetih
 * obroka. Podatke i izbor firme daje ekran (stari tab: `data`, `companies`, `selectedCompany`); izbor
 * firme se vraća ekranu događajem `select-company`.
 */
export default {
  name: "GuestMobileUsersView",
  props: {
    rows: { type: Array, default: () => [] },
    companies: { type: Array, default: () => [] },
    company: { type: String, default: null },
    loading: { type: Boolean, default: false },
    loadingCompanies: { type: Boolean, default: false },
    search: { type: String, default: "" },
  },
  data: () => ({
    sheet: false,
  }),
  computed: {
    /** Korisnici firme po abecedi; pretraga po imenu, e-pošti, telefonu, kartici i broju. */
    users() {
      const rows = this.rows.map((row, index) => {
        const name = [this.toLatin(row.name || ""), this.toLatin(row.lastname || "")].filter(Boolean).join(" ").trim();
        const email = row.email || (row.card_code ? `${row.card_code}@sportek` : "");
        const ordered = Number(row.order_count) || 0;
        const taken = Number(row.order_payed) || 0;
        return {
          key: `${row.id}-${index}`,
          id: row.id,
          name,
          email,
          initials: [row.name, row.lastname]
            .filter(Boolean)
            .map((part) => String(part).charAt(0))
            .join(""),
          ordered,
          taken,
          complete: ordered > 0 && taken >= ordered,
          searchText: [name, email, row.phone, row.card_code, row.id].filter(Boolean).join(" "),
        };
      });
      const found = this.search ? this.customSearch(rows, [{ value: "searchText" }], this.search) : rows;
      return [...found].sort((a, b) => a.name.localeCompare(b.name));
    },
    totals() {
      return this.users.reduce(
        (sum, user) => ({ ordered: sum.ordered + user.ordered, taken: sum.taken + user.taken }),
        { ordered: 0, taken: 0 }
      );
    },
  },
  methods: {
    pick(name) {
      this.sheet = false;
      if (name !== this.company) {
        this.$emit("select-company", name);
      }
    },
  },
};
</script>

<style lang="scss">
.rm-theme .rm-gu-company {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 60px;
  padding: 10px 14px;
  text-align: left;
  color: var(--rm-text);

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 16px;
    font-weight: 700;
    overflow-wrap: anywhere;
  }
}

.rm-gu-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  .rm-avatar {
    width: 36px;
    height: 36px;
    font-size: 13px;
  }

  &__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  &__email {
    overflow-wrap: anywhere;
  }

  &__counts {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 14px;
    margin-top: 4px;
    font-size: 13px;
  }

  &__id {
    margin-left: auto;
  }
}

.rm-gu-count {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--rm-chip-text);

  b {
    color: var(--rm-text);
    font-size: 14px;
  }

  &.is-complete b {
    color: #15803d;
  }
}

.rm-theme .rm-gu-companies {
  max-height: 60vh;
  overflow-y: auto;

  &__name {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    text-align: left;
  }
}
</style>
